import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Plus, Box, User, Mountain, Palette, Cpu, SlidersHorizontal, Zap, Clapperboard, Combine, Globe, Upload, Loader2, UserRound, Type, Shirt, ChevronDown, ChevronRight } from 'lucide-react';
import { KIND_META, MODIFIER_PALETTE, CONCEPT_SUBJECTS } from './nodes';
import { uploadAssetFile } from '../../lib/assetUpload';
import { toast } from '../../hooks/use-toast';

const ASSET_TABS = [
  { type: 'product', Icon: Box },
  { type: 'model', Icon: User },
  { type: 'environment', Icon: Mountain },
  { type: 'outfit', Icon: Shirt },
];

// Ready-made Combine relations covering the usual ways two subjects sit
// together in a shot, not just unboxing. Dropping one lands a Combine node
// with the relation already written; it stays editable on the node.
const COMBINE_PRESETS = [
  {
    label: 'Inside open box',
    relation: 'the product placed inside the open product box, clearly visible, premium unboxing composition, realistic product photography, clean arrangement, open packaging',
  },
  {
    label: 'Lifted out',
    relation: 'the product being lifted out of its open box, caught mid-motion, the packaging still visible below, realistic product photography',
  },
  {
    label: 'Held in hand',
    relation: 'the product held in a hand and presented to camera, natural grip, correct scale, realistic product photography, uncluttered background',
  },
  {
    label: 'Next to',
    relation: 'the two subjects placed side by side on the same surface, neither overlapping, consistent lighting, scale and perspective across both, realistic product photography',
  },
  {
    label: 'Worn by model',
    relation: 'the product worn by the model, sitting naturally on the body with correct fit, scale and proportion, realistic fashion photography',
  },
  {
    label: 'Stacked',
    relation: 'several units of the product stacked neatly on top of each other, edges aligned, consistent lighting across the stack, realistic product photography',
  },
  {
    label: 'On its screen',
    relation: "the artwork from the second item is shown on the screen of the first, fitted inside the visible screen edges and following the screen's own perspective and proportions, reading as content genuinely displayed on that display rather than pasted over it; the device itself stays unchanged in design, frame, buttons, position, lighting and reflections",
  },
  {
    label: 'Printed on it',
    relation: 'the artwork from the second item is printed on the surface of the first, following the curve and material of that surface with realistic ink, weave or finish, correct scale and perspective, never floating flat on top',
  },
  {
    label: 'Poured / applied',
    relation: 'a small amount of the product is dispensed from its container and applied or poured, the substance keeping its own texture and colour, the container clearly identifiable in frame, realistic material physics',
  },
  {
    label: 'In use',
    relation: 'the subject genuinely using the product the way it is meant to be used, hands and posture correct for that action, caught mid-gesture rather than posed, realistic lifestyle photography',
  },
  {
    label: 'Close to face',
    relation: 'the product held up beside the face at cheek height, both in focus and at true relative scale, the face turned slightly toward camera, close beauty framing',
  },
  {
    label: 'Flat lay',
    relation: 'the items arranged on a flat surface and photographed straight down from above, evenly spaced with deliberate negative space between them, soft even light, no perspective distortion',
  },
  {
    label: 'Before and after',
    relation: 'the same subject shown in two states side by side within one frame, identical framing, lighting and distance in both halves so only the difference between them reads as the change',
  },
];

// Wardrobe swaps. A persistent influencer's reference images fix the clothing as
// firmly as the face, so without one of these every render repeats the same
// outfit. Dropping one lands an Outfit node; the text stays editable.
const OUTFIT_PRESETS = [
  // Labels stay under ~10 characters so they survive the two-column grid; the
  // full wording is on the chip's title and on the node once dropped.
  { label: 'Casual', text: 'a plain well-fitted t-shirt and straight-leg jeans, relaxed everyday styling' },
  { label: 'Cosy knit', text: 'an oversized cream knit sweater over simple trousers, soft cosy autumn styling' },
  { label: 'Smart', text: 'a crisp button-down shirt with the sleeves rolled up and tailored trousers, smart casual styling' },
  { label: 'Dress', text: 'a light floral summer dress with thin straps, easy warm-weather styling' },
  { label: 'Athleisure', text: 'a matching athleisure set, fitted leggings and a cropped training top, sporty styling' },
  { label: 'Business', text: 'a tailored blazer worn open over a plain top, polished business-casual styling' },
  { label: 'Lounge', text: 'a soft matching lounge set in a muted tone, comfortable at-home styling' },
  { label: 'Custom…', text: '' },
];

// Long directives for the Generator. They land as a Custom text node, which is
// the only free-text input the compiler folds into the prompt -- the Generator
// node itself carries no text. The creator's identity is deliberately left out:
// the compiler already injects "the exact same person as the reference images"
// for every wired influencer, so repeating it here would only fight itself.
const CUSTOM_PRESETS = [
  {
    label: 'Keep artwork exact',
    text: "USE THE SUPPLIED ARTWORK: reproduce the artwork, label, cover or logo from the reference image exactly as supplied. Do not recreate, redesign, reinterpret, regenerate, approximate or replace it. Preserve the original exactly: the same imagery, the same title and any other text, the same typography, the same colours, the same composition, the same graphic elements and the same proportions. Treat it as a fixed asset and place that exact image onto the product, applying only the perspective and surface distortion needed to sit correctly on the material. Do not crop anything meaningful and do not restyle it. It must stay clearly recognisable as the supplied reference.",
  },
  {
    label: 'Keep product unchanged',
    text: "PRESERVE THE PRODUCT: keep the physical object exactly as it appears in the reference image. Same shape, proportions, materials, finish, seams, buttons, bezel and hardware. Same position, angle and perspective. Same lighting, shadows and reflections falling on it. Nothing about the object's construction changes. Only what is explicitly described elsewhere in this prompt may differ.",
  },
  {
    label: 'Studio product photo',
    text: "PHOTOGRAPHY: photorealistic commercial product photography, softbox lighting, 100mm lens at f/8, eye-level angle, clean seamless studio backdrop, crisp product detail, soft even illumination, realistic skin and material texture, professional colour grading, premium catalogue quality, high detail throughout.",
  },
  {
    label: 'Lifestyle UGC photo',
    text: "PHOTOGRAPHY: authentic user-generated look, shot on a modern phone camera, natural window light with soft directional falloff, slightly imperfect handheld framing, real room in the background with shallow depth of field, honest skin texture with no retouching, natural colour, nothing that reads as a studio advert.",
  },
  {
    label: 'No invented text',
    text: "TEXT: render no writing that was not supplied. Do not invent brand names, slogans, labels, price tags, packaging copy or background signage. Where text appears on the supplied reference it must be reproduced exactly as it reads there. Anywhere else, leave surfaces blank rather than filling them with approximated or garbled lettering.",
  },
  {
    label: 'Correct hands',
    text: "ANATOMY: five fingers on each visible hand, correct joint direction, natural grip pressure with the fingers actually contacting the object and slight compression where they press. No extra or missing digits, no fused or bent-back fingers, no floating hands. Wrists and forearms connect plausibly to the body.",
  },
  {
    label: 'No AI tells',
    text: "REALISM: avoid the usual generated-image giveaways. No waxy or plastic skin, no symmetrical face, no over-sharpened halo edges, no impossible reflections, no melted or duplicated background objects, no jewellery or seams that merge into skin. Keep pores, stray hairs, fabric wrinkles and small asymmetries. Lighting must come from one consistent, physically plausible source.",
  },
];

const WIDTH_KEY = 'idolaris_panel_width';
const OPEN_KEY = 'idolaris_panel_open';
const TAB_KEY = 'idolaris_panel_tab';
const MIN_W = 240;
const MAX_W = 560;

// Nine sections stacked open at once left every one of them too short to read,
// and the palette at the bottom pushed the asset list down to two and a half
// rows. Closed is the default for the ones that are reference material rather
// than daily tools; whatever the creator opens is remembered.
const DEFAULT_OPEN = {
  quickstart: true, sources: false, styles: true, modifiers: true,
  outfit: false, combine: false, presets: false, output: true,
};

function readOpen() {
  try {
    return { ...DEFAULT_OPEN, ...JSON.parse(localStorage.getItem(OPEN_KEY) || '{}') };
  } catch { return { ...DEFAULT_OPEN }; }
}

function Section({ id, title, icon, hint, open, onToggle, children }) {
  const isOpen = !!open[id];
  return (
    <div className="border-t border-[#2A2A30] first:border-t-0">
      <button
        onClick={() => onToggle(id)}
        data-testid={`panel-section-${id}`}
        className="flex w-full items-center gap-1.5 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-white/50 transition-colors hover:text-white/80"
      >
        {isOpen ? <ChevronDown size={12} className="shrink-0" /> : <ChevronRight size={12} className="shrink-0" />}
        {icon}
        <span className="truncate">{title}</span>
      </button>
      {isOpen && (
        <div className="pb-3">
          {hint && <p className="mb-2 text-[10px] leading-relaxed text-white/35">{hint}</p>}
          {children}
        </div>
      )}
    </div>
  );
}

function DragChip({ onDragStart, onClick, color, children, compact, title }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      title={title || 'Click to add, or drag onto the canvas'}
      className={`flex cursor-pointer items-center gap-2 rounded-lg border border-[#2A2A30] bg-[#141417] transition-colors hover:border-white/25 hover:bg-[#1A1A1E] active:cursor-grabbing ${compact ? 'px-2.5 py-1.5 text-xs text-white/80' : 'px-3 py-2 text-sm text-white/85'}`}
    >
      <span className={`shrink-0 rounded-full ${compact ? 'h-2 w-2' : 'h-2.5 w-2.5'}`} style={{ background: color }} />
      <span className="truncate">{children}</span>
      <Plus size={compact ? 12 : 13} className="ml-auto shrink-0 text-white/30" />
    </div>
  );
}

export default function LeftPanel({ assets, templates, influencers = [], onAssetUploaded, onAddNode }) {
  const [tab, setTab] = useState('product');
  const [q, setQ] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Tres pestañas en vez de un scroll infinito: Add (paleta), Assets
  // (biblioteca), People (influencers). La ultima usada se recuerda.
  const [panelTab, setPanelTab] = useState(() => {
    try {
      const t = localStorage.getItem(TAB_KEY);
      return ['add', 'assets', 'people'].includes(t) ? t : 'add';
    } catch { return 'add'; }
  });
  const pickPanelTab = (t) => {
    setPanelTab(t);
    try { localStorage.setItem(TAB_KEY, t); } catch { /* private mode */ }
  };

  const [open, setOpen] = useState(readOpen);
  const toggle = useCallback((id) => {
    setOpen((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(OPEN_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }, []);

  // Dragged rather than fixed, because how much room this deserves depends on
  // what the creator is doing: picking an influencer wants width, wiring a
  // graph wants the canvas.
  const [width, setWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem(WIDTH_KEY) || '', 10);
    return saved >= MIN_W && saved <= MAX_W ? saved : 288;
  });
  const dragging = useRef(false);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      e.preventDefault();
      const w = Math.min(MAX_W, Math.max(MIN_W, e.clientX));
      setWidth(w);
    };
    const up = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try { localStorage.setItem(WIDTH_KEY, String(width)); } catch { /* private mode */ }
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [width]);

  const startDrag = () => {
    dragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // Two columns only once a chip can hold a whole word. At 288 it could not,
  // which is where Composition became "Compo…" and Expression "Expres…".
  const twoUp = width >= 340;
  const chipGrid = twoUp ? 'grid grid-cols-2 gap-1.5' : 'grid grid-cols-1 gap-1.5';

  const setDrag = (payload) => (e) => {
    e.dataTransfer.setData('application/idolaris', JSON.stringify(payload));
    // A second entry carrying only the node type. The payload itself cannot be
    // read while a drag is in flight, browsers only expose the list of formats,
    // so the type is smuggled in as a format name to let the canvas light up
    // the node this would land in. Uploads and influencers only: those are the
    // things that stand in for something already on the canvas.
    if (payload.group === 'asset' || payload.group === 'influencer') {
      e.dataTransfer.setData(`idolaris/${payload.type}`, '');
    }
    e.dataTransfer.effectAllowed = 'move';
  };
  const add = (payload) => () => { if (onAddNode) onAddNode(payload); };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      await uploadAssetFile(file, tab);
      toast({ title: 'Asset uploaded', description: 'Added to your library.' });
      if (onAssetUploaded) await onAssetUploaded();
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message });
    }
    setUploading(false);
  };

  const filtered = assets.filter((a) => a.type === tab && a.name.toLowerCase().includes(q.toLowerCase()));
  const TabIcon = KIND_META[tab].Icon;

  return (
    <aside style={{ width }} className="relative flex h-full shrink-0 flex-col overflow-y-auto border-r border-[#2A2A30] bg-[#0E0E10] no-scrollbar">
      {/* Grab anywhere on the right edge to widen it. */}
      <div
        onMouseDown={startDrag}
        onDoubleClick={() => { setWidth(288); try { localStorage.setItem(WIDTH_KEY, '288'); } catch { /* private mode */ } }}
        title="Drag to resize, double click to reset"
        data-testid="panel-resize"
        className="fixed top-0 z-20 h-full w-1.5 cursor-col-resize hover:bg-white/20"
        style={{ left: width - 3 }}
      />

      {/* Quick start: una linea con chevron. Su estado vive en el mismo
          OPEN_KEY de siempre, asi que si el usuario lo cerro una vez arranca
          cerrado. */}
      <div className="shrink-0 border-b border-[#2A2A30] px-3">
        <Section id="quickstart" title="Quick start" open={open} onToggle={toggle}
          icon={<Zap size={12} className="shrink-0 text-node-style" />}>
          <p className="mb-2 text-[11px] leading-relaxed text-white/45">Drag a subject, then a Style, then the Generator onto the canvas. Wire them: <span className="text-node-product">Subject</span> → <span className="text-node-generator">Generator</span>, <span className="text-node-style">Style</span> → <span className="text-node-generator">Generator</span>.</p>
          <div className={twoUp ? 'grid grid-cols-3 gap-1.5' : 'grid grid-cols-2 gap-1.5'}>
            {CONCEPT_SUBJECTS.map((c) => (
              <DragChip key={c.type} color={KIND_META[c.kind].color} onDragStart={setDrag({ group: 'concept', type: c.type, data: c.data })} onClick={add({ group: 'concept', type: c.type, data: c.data })}>
                {c.label}
              </DragChip>
            ))}
          </div>
          <p className="mt-1.5 text-[10px] text-white/35">No upload needed, edit the name after dropping.</p>
        </Section>
      </div>

      {/* Las tres pestañas: paleta, biblioteca y personas. Antes era todo un
          unico scroll y la paleta del fondo no se descubria nunca. */}
      <div className="shrink-0 border-b border-[#2A2A30] p-2">
        <div className="grid grid-cols-3 gap-1">
          {[
            { k: 'add', l: 'Add', Icon: Plus },
            { k: 'assets', l: 'Assets', Icon: Box },
            { k: 'people', l: 'People', Icon: UserRound },
          ].map(({ k, l, Icon }) => (
            <button
              key={k}
              onClick={() => pickPanelTab(k)}
              data-testid={`panel-tab-${k}`}
              className={`flex items-center justify-center gap-1.5 rounded-full px-2 py-1.5 text-xs font-medium transition-colors ${
                panelTab === k ? 'bg-white text-black' : 'text-white/60 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Icon size={13} /> {l}
            </button>
          ))}
        </div>
      </div>

      {panelTab === 'assets' && (
        <>
          <div className="shrink-0 border-b border-[#2A2A30] p-3">
            <p className="mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-white/50">
              <span>Your assets</span>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="studio-upload-asset"
                className="inline-flex items-center gap-1 rounded-md border border-[#2A2A30] bg-[#141417] px-2 py-1 text-[10px] font-medium normal-case tracking-normal text-white/80 transition-colors hover:border-white/30 disabled:opacity-50"
              >
                {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Upload
              </button>
            </p>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} />
            {/* Four across only when they fit: on a narrow panel one row squeezed
                "environment" to an ellipsis. */}
            <div className={`mb-2 grid gap-1 ${width >= 420 ? 'grid-cols-4' : 'grid-cols-2'}`}>
              {ASSET_TABS.map(({ type, Icon }) => (
                <button
                  key={type}
                  onClick={() => setTab(type)}
                  className={`flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs capitalize transition-colors ${
                    tab === type ? 'bg-white text-black' : 'text-white/60 hover:bg-white/5'
                  }`}
                >
                  <Icon size={13} /> {type}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search assets"
                className="w-full rounded-md border border-[#2A2A30] bg-[#141417] py-1.5 pl-8 pr-2 text-xs text-white placeholder:text-white/40 focus:border-white/30 focus:outline-none"
              />
            </div>
          </div>

          <div className="shrink-0 space-y-2 p-3">
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/50">
              <TabIcon size={12} style={{ color: KIND_META[tab]?.color }} /> {tab} assets
            </p>
            {filtered.length === 0 && (
              <p className="px-1 py-4 text-center text-xs text-white/40">No {tab} assets yet. Tap <span className="text-white/70">Upload</span> above to add one.</p>
            )}
            <div className="space-y-2">
              {filtered.map((a) => (
                <div
                  key={a.id}
                  draggable
                  onDragStart={setDrag({ group: 'asset', type: `asset.${a.type}`, data: { kind: a.type, assetId: a.id, name: a.name, thumb: a.url } })}
                  onClick={add({ group: 'asset', type: `asset.${a.type}`, data: { kind: a.type, assetId: a.id, name: a.name, thumb: a.url } })}
                  title="Click to add, or drag onto the canvas"
                  data-testid={`asset-chip-${a.id}`}
                  className="group flex cursor-pointer items-center gap-2 rounded-lg border border-[#2A2A30] bg-[#141417] p-2 transition-colors hover:border-white/25 hover:bg-[#1A1A1E] active:cursor-grabbing"
                >
                  <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md border border-[#2A2A30]">
                    {a.url && <img src={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-white">{a.name}</p>
                    <p className="truncate text-[10px] text-white/40">{a.status === 'ready' ? 'analysed' : 'analysing…'}</p>
                  </div>
                  <Plus size={13} className="shrink-0 text-white/30" />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {panelTab === 'people' && (
        <div className="shrink-0 space-y-2 p-3">
          <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/50">
            <UserRound size={12} style={{ color: KIND_META.influencer.color }} /> Your people
          </p>
          {influencers.length === 0 && (
            <p className="px-1 py-4 text-center text-xs text-white/40">No people yet. Create one on the People page.</p>
          )}
          <div className="space-y-2">
            {influencers.map((inf) => (
              <div
                key={inf.id}
                draggable
                onDragStart={setDrag({ group: 'influencer', type: 'asset.influencer', data: { kind: 'influencer', influencerId: inf.id, name: inf.name, thumb: inf.cover_url, persona: inf.persona } })}
                onClick={add({ group: 'influencer', type: 'asset.influencer', data: { kind: 'influencer', influencerId: inf.id, name: inf.name, thumb: inf.cover_url, persona: inf.persona } })}
                title="Click to add, or drag onto the canvas"
                data-testid={`influencer-chip-${inf.id}`}
                className="group flex cursor-pointer items-center gap-2 rounded-lg border border-[#2A2A30] bg-[#141417] p-2 transition-colors hover:border-node-model/50 hover:bg-[#1A1A1E] active:cursor-grabbing"
              >
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md border border-[#2A2A30]">
                  {inf.cover_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${inf.cover_url}`} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center"><UserRound size={14} className="text-white/30" /></div>}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-white">{inf.name}</p>
                  <p className="truncate text-[10px] text-white/40">{(inf.references || []).length} refs · consistent</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {panelTab === 'add' && (
      /* Ordered the way a graph is actually built: sources feed the look, the
          look feeds composition, composition feeds instructions, and the
          generators sit at the end because they are the output. */
      <div className="shrink-0 px-3">
        <Section id="sources" title="In the photo" open={open} onToggle={toggle}
          icon={<Globe size={12} className="shrink-0" style={{ color: KIND_META.website.color }} />}>
          <DragChip color={KIND_META.website.color}
            onDragStart={setDrag({ group: 'website', type: 'asset.website', data: { kind: 'website', url: '', sceneMode: 'influencer' } })}
            onClick={add({ group: 'website', type: 'asset.website', data: { kind: 'website', url: '', sceneMode: 'influencer' } })}>
            <span className="inline-flex items-center gap-1.5"><Globe size={13} /> Website</span>
          </DragChip>
        </Section>

        <Section id="styles" title={`Styles · ${templates.length}`} open={open} onToggle={toggle}
          icon={<Palette size={12} className="shrink-0" style={{ color: KIND_META.style.color }} />}>
          {/* No max-height any more: the section closes instead of being a
              scroll box inside a scroll box, which hid most of the list. */}
          <div className="grid grid-cols-1 gap-1.5">
            {templates.map((t) => (
              <DragChip key={t.slug} color={t.accent} onDragStart={setDrag({ group: 'style', type: 'asset.style', data: { kind: 'style', templateSlug: t.slug, name: t.name, accent: t.accent, intensity: 0.8 } })} onClick={add({ group: 'style', type: 'asset.style', data: { kind: 'style', templateSlug: t.slug, name: t.name, accent: t.accent, intensity: 0.8 } })}>
                {t.name}
              </DragChip>
            ))}
          </div>
        </Section>

        <Section id="modifiers" title="Adjustments" open={open} onToggle={toggle}
          icon={<SlidersHorizontal size={12} className="shrink-0" />}>
          <div className={chipGrid}>
            {MODIFIER_PALETTE.map((m) => (
              <DragChip key={m.type} color={KIND_META[m.kind].color} title={m.label} onDragStart={setDrag({ group: 'modifier', type: m.type, data: m.data })} onClick={add({ group: 'modifier', type: m.type, data: m.data })}>
                {m.label}
              </DragChip>
            ))}
          </div>
        </Section>

        <Section id="outfit" title="Outfit" open={open} onToggle={toggle}
          icon={<Shirt size={12} className="shrink-0" style={{ color: KIND_META.outfit.color }} />}
          hint={<>Changes the clothing without touching the face. For a specific garment, upload it in the <span className="text-white/60">Outfit</span> tab above and drag it in.</>}>
          <div className={chipGrid}>
            {OUTFIT_PRESETS.map((o) => (
              <DragChip key={o.label} color={KIND_META.outfit.color} compact title={o.text || 'Type your own outfit on the node'}
                onDragStart={setDrag({ group: 'modifier', type: 'modifier.outfit', data: { kind: 'outfit', text: o.text } })}
                onClick={add({ group: 'modifier', type: 'modifier.outfit', data: { kind: 'outfit', text: o.text } })}>
                {o.label}
              </DragChip>
            ))}
          </div>
        </Section>

        <Section id="combine" title="How things relate" open={open} onToggle={toggle}
          icon={<Combine size={12} className="shrink-0" style={{ color: KIND_META.combine.color }} />}
          hint="Quick presets. The relation stays editable on the node.">
          <div className="space-y-1.5">
            {COMBINE_PRESETS.map((c) => (
              <DragChip key={c.label} color={KIND_META.combine.color} compact title={c.relation}
                onDragStart={setDrag({ group: 'combine', type: 'combine.scene', data: { kind: 'combine', relation: c.relation } })}
                onClick={add({ group: 'combine', type: 'combine.scene', data: { kind: 'combine', relation: c.relation } })}>
                {c.label}
              </DragChip>
            ))}
          </div>
        </Section>

        <Section id="presets" title="Prompt presets" open={open} onToggle={toggle}
          icon={<Type size={12} className="shrink-0" style={{ color: KIND_META.custom.color }} />}
          hint="Drops a Custom text node. Wire it into the Generator.">
          <div className="space-y-1.5">
            {CUSTOM_PRESETS.map((c) => (
              <DragChip key={c.label} color={KIND_META.custom.color} compact title={c.text}
                onDragStart={setDrag({ group: 'modifier', type: 'modifier.custom', data: { kind: 'custom', text: c.text } })}
                onClick={add({ group: 'modifier', type: 'modifier.custom', data: { kind: 'custom', text: c.text } })}>
                {c.label}
              </DragChip>
            ))}
          </div>
        </Section>

        <Section id="output" title="Output" open={open} onToggle={toggle}
          icon={<Cpu size={12} className="shrink-0" />}>
          <div className="space-y-1.5">
            <DragChip color={KIND_META.generator.color}
              onDragStart={setDrag({ group: 'generator', type: 'generator.image', data: { kind: 'generator', quality: 'premium', status: 'idle' } })}
              onClick={add({ group: 'generator', type: 'generator.image', data: { kind: 'generator', quality: 'premium', status: 'idle' } })}>
              <span className="inline-flex items-center gap-1.5"><Cpu size={13} /> Image Generator</span>
            </DragChip>
            <DragChip color={KIND_META.video.color}
              onDragStart={setDrag({ group: 'generator', type: 'generator.video', data: { kind: 'video', model: 'fast', duration: 8, aspect: '9:16', prompt: '', status: 'idle' } })}
              onClick={add({ group: 'generator', type: 'generator.video', data: { kind: 'video', model: 'fast', duration: 8, aspect: '9:16', prompt: '', status: 'idle' } })}>
              <span className="inline-flex items-center gap-1.5"><Clapperboard size={13} /> Video Generator</span>
            </DragChip>
          </div>
        </Section>
      </div>
      )}
    </aside>
  );
}
